/*
	Common state and reducers associated with filtering large sets of data, tailored to Flashtalking APIs,
	which use these conventionally as request parameters for collections (Libraries, Creatives, etc.).

	Call `addFiltering()` with a reducerConfig object (see readme in reducers directory), to add
		paging,
		RPP (results per page),
		basic search,
		sorting,
		advanced filtering (queries on columns, etc.)

	This will fold in the following state:
		{
			filterInfo: {
				page, // integer
				rpp, // integer
				searchTerm, // string
				sort, // string
				advancedFilter, // string
			}
		}

	The following action types:
		{
			`${typeRoot}_CHANGE_PAGE`; // e.g. LIBRARIES_CHANGE_PAGE
			`${typeRoot}_CHANGE_RPP`;
			`${typeRoot}_CHANGE_SEARCH_TERM`;
			`${typeRoot}_CHANGE_SORT`;
			`${typeRoot}_CHANGE_ADVANCED_FILTER`;
		}

	The following action creators:
		setPage(page),
		setRPP(rpp),
		setSearchTerm(searchTerm),
		setSort(sortBy, sortOrder), // Setting `sort` requires `sortOrder` be set as "ASC" or "DESC".
		setAdvancedFilter(filter),

	Reducers are provided that only touch the Filtering state.
		Setting any value that isn't `page` will reset `page` to 1.

	If you need a custom set of default state, you can specify it as the second parameter of `addFiltering()`,
	and it will be folded into the `filterInfo` object.
		e.g.
			libraryConfig = addFiltering(libraryConfig, { sort: "lastModified:DESC" });
*/

import { DEFAULT_RPP } from "utils/constants";

export const FILTER_NAMESPACE = "filterInfo";

/*
	Filtering default state
*/

export const defaultFilterState = {
	rpp:        DEFAULT_RPP,
	page:       1,
	searchTerm: "",
	sort:       "",
	advancedFilter: "",
};


/*
	Action Creator Generators
*/

export function generateSetPage(type){
	return function setPage(page){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { page },
			});
		};
	};
}

export function generateSetRPP(type){
	return function setRPP(rpp){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { rpp },
			});
		};
	};
}

export function generateSetSearchTerm(type){
	return function setSearchTerm(searchTerm){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { searchTerm },
			});
		};
	};
}

export function generateSetSort(type){
	return function setSort(sortBy, sortOrder){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { sortBy, sortOrder },
			});
		};
	};
}

export function generateSetAdvancedFilter(type){
	return function setAdvancedFilter(advancedFilter){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { advancedFilter },
			});
		};
	};
}

export function generateResetAllFilters(type){
	return function resetAllFilters(){
		return (dispatch) => {
			dispatch({ type });
		};
	};
}


/*
	Reducers (action handlers)
*/

export function updatePage(state, action){
	let { page } = action.payload;
	return {
		...state,
		[FILTER_NAMESPACE]:{
			...state[FILTER_NAMESPACE],
			page,
		},
	};
}

export function updateRPP(state, action){
	let { rpp } = action.payload;
	return {
		...state,
		[FILTER_NAMESPACE]:{
			...state[FILTER_NAMESPACE],
			rpp,
			page: 1,
		},
	};
}

export function updateSearchTerm(state, action){
	let { searchTerm } = action.payload;
	return {
		...state,
		[FILTER_NAMESPACE]:{
			...state[FILTER_NAMESPACE],
			searchTerm,
			page: 1,
		},
	};
}

const validSortOptions = [
	/*
		These come from react-data-grid, when you click on sortable column headers.
		On the third click, it will set sortOrder to "NONE", which you don't actually
		need to store explicitly, and don't want to send to the API.
	*/
	"ASC",
	"DESC"
];

export function updateSort(state, action){
	let { sortBy, sortOrder } = action.payload;
	let sort = (validSortOptions.includes(sortOrder)) ? `${sortBy}:${sortOrder}` : "";
	return {
		...state,
		[FILTER_NAMESPACE]:{
			...state[FILTER_NAMESPACE],
			sort,
			page: 1,
		},
	};
}


/*
	The advancedFilter syntax is fairly complicated, and enforcing it is outside the
	scope of this redux slice.  If we decide we want to, and for reference, these are
	the valid values.

	Filter operations generally take a column name as the first argument, and relevant
	arguments after that.

	e.g. "eq(name,Bob)" will respond with objects where the `name` is equal to "Bob"

	Full documentation can be found near the top here:
	http://qa01-jcrm.crm.irl11.ftdns.net/swagger-ui.html

	const validFilterOperations = [
		"eq", // equals
		"lt", // <
		"lte", // <=
		"gt", // >
		"gte", // >=
		"neq", // not equals
		"is_null",
		"is_not_null",
		"in", // in set of values
		"not_in", // not in set of values
		"like", // inclusive search with wildcards `*`
		"not_like", // exclusive search with wildcards
		"between", // between two values e.g. "between(id,10,20)"
		"not_between",
	];

	const validFilterSetOperations = [
		"any",
		"all",
	];
*/

export function updateAdvancedFilter(state, action){
	let { advancedFilter } = action.payload;

	return {
		...state,
		[FILTER_NAMESPACE]: {
			...state[FILTER_NAMESPACE],
			advancedFilter,
			page: 1,
		},
	};
}

export function clearAllFilters(state, action){
	return {
		...state,
		[FILTER_NAMESPACE]:{
			searchTerm:"",
			advancedFilter:"",
			sort:state[FILTER_NAMESPACE].sort,
			rpp:DEFAULT_RPP,
			page:1,
		},
	};
}

export function addFiltering(
	{
		typeRoot,
		actionTypes,
		initialState,
		handlers,
		actionCreators,
	},
	customDefaults = {}
){
	let newInitialState = {
		...initialState,
		[FILTER_NAMESPACE]: {
			...defaultFilterState,
			...customDefaults,
		},
	};

	let setPageType = `${typeRoot}_CHANGE_PAGE`;
	let setRPPType = `${typeRoot}_CHANGE_RPP`;
	let setSearchType = `${typeRoot}_CHANGE_SEARCH_TERM`;
	let setSortType = `${typeRoot}_CHANGE_SORT`;
	let setAdvancedFilterType = `${typeRoot}_CHANGE_ADVANCED_FILTER`;
	let resetAllFilters = `${typeRoot}_RESET_ALL_FILTERS`;

	let newTypes = {
		...actionTypes,
		[setPageType]: setPageType,
		[setRPPType]: setRPPType,
		[setSearchType]: setSearchType,
		[setSortType]: setSortType,
		[setAdvancedFilterType]: setAdvancedFilterType,
	};

	let newActionCreators = {
		...actionCreators,
		setPage: generateSetPage(setPageType),
		setRPP: generateSetRPP(setRPPType),
		setSearchTerm: generateSetSearchTerm(setSearchType),
		setSort: generateSetSort(setSortType),
		setAdvancedFilter: generateSetAdvancedFilter(setAdvancedFilterType),
		resetAllFilters: generateResetAllFilters(resetAllFilters),
	};

	let newHandlers = {
		...handlers,
		[setPageType]: updatePage,
		[setRPPType]: updateRPP,
		[setSearchType]: updateSearchTerm,
		[setSortType]: updateSort,
		[setAdvancedFilterType]: updateAdvancedFilter,
		[resetAllFilters]: clearAllFilters,
	};

	return {
		typeRoot,
		types: newTypes,
		actionCreators: newActionCreators,
		initialState: newInitialState,
		handlers: newHandlers,
	};
}
